import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  Business as BusinessIcon,
  FolderOpen as FolderOpenIcon,
  PersonOutline as PersonOutlineIcon,
  Refresh as RefreshIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import {
  BodyText,
  CaptionText,
  CardTitle,
  CompactPageTitle,
  OverlineText,
  SectionTitle,
} from '../../../shared/ui/typography';
import {
  SettingsType,
  type SettingInfoDto,
} from '../../../shared/api/pipeex/pcfSettingsClient';
import { OperationProgressIndicator } from '../../../shared/ui/progress';
import type { MappingProfileProjectMatch } from '../lib/mappingProjectReadiness';
import type { MappingProfileProjectCatalogSource } from '../lib/projectProfileGateTypes';
import { buildProfileKey, parseSettingsType } from '../lib/profile';
import { formatMissingParameterPreview } from '../lib/formatMissingParameterPreview';
import ProfileContentsSection from './ProfileContentsSection';
import { borderRadius, layoutGap } from '../../../theme/theme';
import {
  getWorkflowWorkspacePalette,
  WorkspaceStatusPill,
  type WorkspaceTone,
} from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';

interface MappingProjectProfileGateDialogProps {
  open: boolean;
  activeDocumentName: string | null;
  projectKey: string | null;
  profiles: SettingInfoDto[];
  profilesLoading: boolean;
  profileMatches?: MappingProfileProjectMatch[];
  profileMatchesLoading?: boolean;
  topMatchProfileKey?: string | null;
  lastLoadedProfileKey?: string | null;
  catalogReady?: boolean;
  catalogSource?: MappingProfileProjectCatalogSource;
  catalogError?: string | null;
  selectedSettingsType: SettingsType;
  selectedProfileKey: string;
  isSaving: boolean;
  profileError: string | null;
  onSelectedProfileKeyChange: (profileKey: string) => void;
  onRefreshProfiles: () => void;
  onReinitiateWorkflow: () => void;
  onConfirm: () => void;
}

const getProfileScopeLabel = (settingsType: SettingsType): string =>
  settingsType === SettingsType.Company ? 'Company' : 'User';

const getMatchTone = (match: MappingProfileProjectMatch | null): WorkspaceTone => {
  if (!match) {
    return 'neutral';
  }

  if (match.status === 'match') {
    return 'success';
  }

  if (match.status === 'partial') {
    return 'warning';
  }

  if (match.status === 'error') {
    return 'error';
  }

  return 'neutral';
};

const getMatchLabel = (match: MappingProfileProjectMatch | null, isLoading: boolean): string => {
  if (isLoading) {
    return 'Checking project';
  }

  if (!match) {
    return 'No project evidence';
  }

  if (match.status === 'match') {
    return 'Complete match';
  }

  if (match.status === 'partial') {
    return `${match.availableCount}/${match.referenceCount}`;
  }

  if (match.status === 'missing') {
    return 'Not present';
  }

  if (match.status === 'empty') {
    return 'No saved parameters';
  }

  return 'Inspection failed';
};

const formatCoverage = (match: Pick<MappingProfileProjectMatch, 'availableCount' | 'referenceCount'>): string =>
  `${match.availableCount} of ${match.referenceCount}`;

const getMatchEvidenceDetail = (input: {
  isLoading: boolean;
  catalogError: string | null;
  topMatch: MappingProfileProjectMatch | null;
  topMatchHasAvailability: boolean;
  exactMatchCount: number;
  catalogSource: MappingProfileProjectCatalogSource;
}): string => {
  if (input.isLoading) {
    return 'PipeEX is comparing each profile’s saved parameters with the parameters in the active Revit project.';
  }

  if (input.catalogError) {
    return `${input.catalogError} Project evidence is incomplete; open the Revit project fully, then refresh profiles.`;
  }

  if (input.topMatchHasAvailability && input.topMatch) {
    if (input.exactMatchCount > 0 && input.topMatch.status === 'match') {
      return `Complete match: the active Revit project contains all ${input.topMatch.referenceCount} parameters saved in “${input.topMatch.name}.”`;
    }

    const missingLabel = input.topMatch.missingCount === 1 ? 'is' : 'are';
    return `Best available match: the active Revit project contains ${formatCoverage(input.topMatch)} parameters saved in “${input.topMatch.name}”; ${input.topMatch.missingCount} ${missingLabel} missing and will be added if you apply this profile.`;
  }

  if (input.catalogSource === 'nonHost') {
    return 'Local mode cannot read Revit project parameters, so PipeEX cannot verify profile coverage. Select a profile only for UI testing.';
  }

  return 'No profile fully matches the active Revit project. Review the missing parameters before applying a profile.';
};

const getSelectedMatchSummary = (match: MappingProfileProjectMatch | null): string => {
  if (!match) {
    return 'Project coverage is not available yet. PipeEX will check the active Revit project when you apply this profile.';
  }

  if (match.status === 'match') {
    return `Complete match: all ${match.referenceCount} parameters saved in this profile are present in the active Revit project.`;
  }

  if (match.status === 'partial') {
    return `Partial match: the active Revit project contains ${formatCoverage(match)} saved parameters; applying this profile will add ${match.missingCount} missing parameter${match.missingCount === 1 ? '' : 's'}.`;
  }

  if (match.status === 'missing') {
    return 'The active Revit project contains none of this profile’s saved parameters; applying it will add the missing PipeEX parameters.';
  }

  if (match.status === 'empty') {
    return 'This profile contains no saved parameter references, so it provides no project-readiness evidence.';
  }

  return match.error || 'PipeEX could not inspect this profile. Refresh profiles, then try again.';
};

export default function MappingProjectProfileGateDialog({
  open,
  activeDocumentName,
  projectKey,
  profiles,
  profilesLoading,
  profileMatches = [],
  profileMatchesLoading = false,
  topMatchProfileKey = null,
  lastLoadedProfileKey = null,
  catalogReady = false,
  catalogSource = 'none',
  catalogError = null,
  selectedSettingsType,
  selectedProfileKey,
  isSaving,
  profileError,
  onSelectedProfileKeyChange,
  onRefreshProfiles,
  onReinitiateWorkflow,
  onConfirm,
}: MappingProjectProfileGateDialogProps): React.ReactElement {
  const theme = useTheme();
  const workspacePalette = getWorkflowWorkspacePalette(theme);
  const [profileSwapAcknowledged, setProfileSwapAcknowledged] = React.useState(false);
  const selectedProfileKeyRef = React.useRef(selectedProfileKey);
  React.useEffect(() => {
    if (selectedProfileKeyRef.current !== selectedProfileKey) {
      selectedProfileKeyRef.current = selectedProfileKey;
      setProfileSwapAcknowledged(false);
    }
  }, [selectedProfileKey]);
  const selectedProfile = useMemo(
    () => profiles.find((profile) => buildProfileKey(profile.name, parseSettingsType(profile.settingsType)) === selectedProfileKey) ?? null,
    [profiles, selectedProfileKey]
  );
  const matchByProfileKey = useMemo(
    () => new Map(profileMatches.map((match) => [match.profileKey, match])),
    [profileMatches]
  );
  const selectedMatch = selectedProfileKey ? matchByProfileKey.get(selectedProfileKey) ?? null : null;
  const exactMatchCount = profileMatches.filter((match) => match.status === 'match').length;
  const topMatch = topMatchProfileKey
    ? matchByProfileKey.get(topMatchProfileKey) ?? null
    : null;
  const topMatchHasAvailability = Boolean(topMatch && topMatch.availableCount > 0);
  const lastLoadedProfile = lastLoadedProfileKey
    ? profiles.find((profile) => buildProfileKey(profile.name, parseSettingsType(profile.settingsType)) === lastLoadedProfileKey) ?? null
    : null;
  const selectedScopeIcon =
    selectedSettingsType === SettingsType.Company ? (
      <BusinessIcon fontSize="small" />
    ) : (
      <PersonOutlineIcon fontSize="small" />
    );
  const statusTone: WorkspaceTone = profileMatchesLoading
    ? 'info'
    : catalogError
      ? 'warning'
      : exactMatchCount > 0
        ? 'success'
        : topMatchHasAvailability
          ? 'warning'
          : 'neutral';
  const statusLabel = profileMatchesLoading
    ? 'Checking project'
    : catalogError
      ? 'Project evidence unavailable'
      : exactMatchCount > 0
        ? 'Complete parameter match'
        : topMatchHasAvailability
          ? 'Partial parameter match'
          : 'No complete match';
  const statusDetail = getMatchEvidenceDetail({
    isLoading: profileMatchesLoading,
    catalogError,
    topMatch,
    topMatchHasAvailability,
    exactMatchCount,
    catalogSource,
  });
  const selectedMissingPreview = selectedMatch
    ? formatMissingParameterPreview(selectedMatch.missingParameterNames)
    : null;
  const selectedMissingTitle = selectedMatch?.missingParameterNames.join(', ') ?? '';
  const topMatchMissingPreview =
    topMatch && topMatch.status === 'partial' && topMatch.missingParameterNames.length > 0
      ? formatMissingParameterPreview(topMatch.missingParameterNames)
      : null;
  const topMatchMissingTitle = topMatch?.missingParameterNames.join(', ') ?? '';

  return (
    <Dialog
      open={open}
      transitionDuration={0}
      disableEscapeKeyDown
      aria-labelledby="mapping-project-profile-gate-title"
      aria-describedby="mapping-project-profile-gate-description"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 500,
          borderRadius: `${borderRadius.lg}px`,
          border: `1px solid ${workspacePalette.paneBorder}`,
          bgcolor: theme.palette.background.paper,
          backgroundImage: 'none',
          boxShadow: `0 18px 42px ${alpha(theme.palette.common.black, 0.2)}`,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        id="mapping-project-profile-gate-title"
        sx={{
          px: 2,
          pt: 1.65,
          pb: 0.7,
          borderBottom: `1px solid ${workspacePalette.paneDivider}`,
        }}
      >
        <Stack spacing={layoutGap.blend}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={layoutGap.row}>
            <OverlineText component="span" sx={{ color: workspacePalette.tertiaryText }}>
              Revit project setup
            </OverlineText>
            <WorkspaceStatusPill label={statusLabel} tone={statusTone} />
          </Stack>
          <CompactPageTitle component="span" sx={{ color: workspacePalette.headingText }}>
            Choose a profile for this project
          </CompactPageTitle>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 2, pt: 1.25, pb: 1.25 }}>
        <Stack spacing={layoutGap.row}>
          <BodyText
            secondary
            id="mapping-project-profile-gate-description"
            sx={{ color: workspacePalette.bodyText }}
          >
            Compare each profile with the active Revit project, then choose which settings to apply.
          </BodyText>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '4.6rem minmax(0, 1fr)',
              columnGap: 1,
              alignItems: 'start',
              py: 0.65,
              px: 0.85,
              border: `1px solid ${workspacePalette.sectionBorder}`,
              borderRadius: `${borderRadius.sm}px`,
              bgcolor: workspacePalette.sectionRaisedBackground,
            }}
          >
            <CaptionText sx={{ color: workspacePalette.tertiaryText }}>Project</CaptionText>
            <Box sx={{ minWidth: 0 }}>
              <CardTitle
                component="span"
                sx={{
                  color: workspacePalette.headingText,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={activeDocumentName || projectKey || 'Current project'}
                noWrap
              >
                {activeDocumentName || projectKey || 'Current project'}
              </CardTitle>
              {activeDocumentName && projectKey ? (
                <CaptionText
                  sx={{
                    color: workspacePalette.tertiaryText,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={projectKey}
                >
                  {projectKey}
                </CaptionText>
              ) : null}
            </Box>
          </Box>

          {profileError ? (
            <Box
              sx={{
                border: `1px solid ${alpha(theme.palette.warning.main, 0.24)}`,
                borderRadius: `${borderRadius.sm}px`,
                bgcolor: alpha(theme.palette.warning.main, 0.06),
                px: 0.85,
                py: 0.55,
              }}
            >
              <BodyText secondary sx={{ color: theme.palette.warning.dark }}>
                {profileError}
              </BodyText>
            </Box>
          ) : null}

          <Box
            sx={{
              display: 'grid',
              gap: layoutGap.dense,
              borderRadius: `${borderRadius.sm}px`,
              border: `1px solid ${
                statusTone === 'success'
                  ? alpha(theme.palette.success.main, 0.2)
                  : statusTone === 'warning'
                    ? alpha(theme.palette.warning.main, 0.22)
                    : workspacePalette.sectionBorder
              }`,
              bgcolor:
                statusTone === 'success'
                  ? alpha(theme.palette.success.main, 0.055)
                  : statusTone === 'warning'
                    ? alpha(theme.palette.warning.main, 0.06)
                    : workspacePalette.sectionBackground,
              px: 0.85,
              py: 0.65,
            }}
          >
            <Stack direction="row" spacing={layoutGap.comfort} alignItems="center" justifyContent="space-between">
              <SectionTitle component="div" sx={{ color: workspacePalette.headingText }}>
                Profile-to-project evidence
              </SectionTitle>
              {profileMatchesLoading ? <OperationProgressIndicator size={14} ariaLabel="Comparing mapping profiles with the active Revit project" /> : null}
            </Stack>
            <BodyText secondary component="div" sx={{ color: workspacePalette.mutedText }}>
              {statusDetail}
            </BodyText>
            {topMatchHasAvailability && topMatch?.status === 'partial' ? (
              <CaptionText component="div" sx={{ color: workspacePalette.tertiaryText }}>
                Coverage compares saved profile parameters with this project. It does not show workflow progress.
              </CaptionText>
            ) : null}
            {topMatchMissingPreview ? (
              <CaptionText
                component="div"
                title={topMatchMissingTitle}
                sx={{ color: workspacePalette.tertiaryText }}
              >
                Missing from the active Revit project: {topMatchMissingPreview}
              </CaptionText>
            ) : null}
          </Box>

          {lastLoadedProfile ? (
            <CaptionText sx={{ color: workspacePalette.tertiaryText, lineHeight: 1.35 }}>
              Previously applied: {lastLoadedProfile.name}. Choose the profile that should replace the current mapping settings.
            </CaptionText>
          ) : null}

          <FormControl
            fullWidth
            size="small"
            disabled={profilesLoading || isSaving || profiles.length === 0}
            sx={{
              '& .MuiInputLabel-root': { fontSize: '0.76rem' },
              '& .MuiOutlinedInput-root': {
                minHeight: 36,
                borderRadius: `${borderRadius.sm}px`,
                fontSize: '0.78rem',
                bgcolor: alpha(theme.palette.background.paper, 0.28),
              },
              '& .MuiSelect-select': {
                py: 0.85,
              },
            }}
          >
            <InputLabel id="mapping-project-profile-select-label">Mapping profile</InputLabel>
            <Select
              labelId="mapping-project-profile-select-label"
              label="Mapping profile"
              value={selectedProfile ? selectedProfileKey : ''}
              MenuProps={{ keepMounted: true }}
              onChange={(event: SelectChangeEvent<string>) => {
                onSelectedProfileKeyChange(event.target.value);
              }}
              renderValue={(value) => {
                const profile = profiles.find((item) => buildProfileKey(item.name, parseSettingsType(item.settingsType)) === value);
                if (!profile) {
                  return profilesLoading ? 'Loading mapping profiles…' : 'Select a mapping profile';
                }

                const settingsType = parseSettingsType(profile.settingsType);
                return `${profile.name} (${getProfileScopeLabel(settingsType)})`;
              }}
            >
              {profiles.map((profile) => {
                const key = buildProfileKey(profile.name, parseSettingsType(profile.settingsType));
                const settingsType = parseSettingsType(profile.settingsType);
                const Icon = settingsType === SettingsType.Company ? BusinessIcon : PersonOutlineIcon;
                const match = matchByProfileKey.get(key) ?? null;
                const matchLabel = getMatchLabel(match, profileMatchesLoading);
                const evidenceLabel =
                  key === topMatchProfileKey && match?.availableCount
                    ? 'Best project match'
                    : key === lastLoadedProfileKey
                      ? 'Previous project profile'
                      : matchLabel;

                return (
                  <MenuItem key={key} value={key} data-testid={`project-profile-option-${key}`}>
                    <Stack direction="row" spacing={layoutGap.row} alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
                      <Icon fontSize="small" sx={{ color: workspacePalette.tertiaryText }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <CardTitle component="span" noWrap sx={{ color: workspacePalette.headingText }}>
                          {profile.name} ({getProfileScopeLabel(settingsType)})
                        </CardTitle>
                        {match && match.referenceCount > 0 ? (
                          <CaptionText component="span" sx={{ color: workspacePalette.tertiaryText, display: 'block' }}>
                            {formatCoverage(match)} saved parameters found in project
                          </CaptionText>
                        ) : null}
                      </Box>
                      <WorkspaceStatusPill label={evidenceLabel} tone={getMatchTone(match)} />
                    </Stack>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {selectedProfile ? (
            <>
              <Stack direction="row" spacing={layoutGap.flow} alignItems="flex-start" sx={{ color: workspacePalette.mutedText }}>
                {selectedScopeIcon}
                <Box sx={{ minWidth: 0 }}>
                  <CaptionText sx={{ display: 'block', lineHeight: 1.35 }}>
                    {getSelectedMatchSummary(selectedMatch)}
                  </CaptionText>
                  {selectedMissingPreview ? (
                    <CaptionText
                      title={selectedMissingTitle}
                      sx={{ color: workspacePalette.tertiaryText, display: 'block', lineHeight: 1.35 }}
                    >
                      Will be added to the active Revit project: {selectedMissingPreview}
                    </CaptionText>
                  ) : null}
                </Box>
              </Stack>
              <ProfileContentsSection profile={selectedProfile} />
              <Box
                sx={{
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.24)}`,
                  borderRadius: `${borderRadius.sm}px`,
                  bgcolor: alpha(theme.palette.warning.main, 0.06),
                  px: 0.85,
                  py: 0.65,
                }}
              >
                <BodyText
                  secondary
                  component="div"
                  sx={{ color: theme.palette.warning.main, mb: 0.5 }}
                >
                  Applying this profile can add missing PipeEX parameters to the active Revit project. It also
                  replaces the current Mapping, Quick Fill, PCF Validation, and PCF export settings.
                </BodyText>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={profileSwapAcknowledged}
                      onChange={(event) => setProfileSwapAcknowledged(event.target.checked)}
                      inputProps={{
                        'aria-label': 'I understand that applying this profile changes the active Revit project and replaces the current PipeEX mapping settings',
                      }}
                    />
                  }
                  label={
                    <CaptionText sx={{ color: theme.palette.warning.dark }}>
                      I understand that applying this profile changes the active Revit project and
                      replaces the current PipeEX mapping settings
                    </CaptionText>
                  }
                />
              </Box>
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 2,
          pb: 1.55,
          pt: 0.2,
          gap: layoutGap.row,
          flexWrap: 'wrap',
          borderTop: `1px solid ${workspacePalette.paneDivider}`,
        }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<RestartAltIcon />}
          onClick={onReinitiateWorkflow}
          disabled={isSaving}
          sx={{
            minHeight: 32,
            borderRadius: `${borderRadius.sm}px`,
            px: 1.15,
          }}
        >
          Revise setup
        </Button>
        <IconButton
          size="small"
          aria-label="Refresh mapping profiles and project evidence"
          onClick={onRefreshProfiles}
          disabled={profilesLoading || isSaving}
          sx={{
            width: 32,
            height: 32,
            borderRadius: `${borderRadius.sm}px`,
            border: `1px solid ${workspacePalette.sectionBorder}`,
            color: workspacePalette.mutedText,
            bgcolor: alpha(theme.palette.background.paper, 0.22),
          }}
        >
          {profilesLoading ? <OperationProgressIndicator size={14} ariaLabel="Refreshing mapping profiles and project evidence" /> : <RefreshIcon fontSize="small" />}
        </IconButton>
        <Button
          size="small"
          variant="contained"
          data-testid="project-profile-gate-confirm"
          startIcon={isSaving ? <OperationProgressIndicator size={14} color="inherit" ariaLabel="Applying profile and checking the active Revit project" /> : <FolderOpenIcon />}
          onClick={onConfirm}
          disabled={!selectedProfileKey || profilesLoading || isSaving || !profileSwapAcknowledged}
          sx={{
            minHeight: 32,
            borderRadius: `${borderRadius.sm}px`,
            px: 1.35,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          Apply profile and verify project
        </Button>
      </DialogActions>
    </Dialog>
  );
}
